import React, { useContext, useEffect, useRef, useState } from "react";
import * as WebChat from "botframework-webchat";
import { useAskEmma } from "../../contexts/AskEmmaContext";
import "../../assets/sass/vendors/copailot.scss";
import { useFileUpload } from "./hooks/useFileUpload";
import CameraCapture from "./CameraCapture";
import IconCloseButton from "../shared/IconCloseButton";

import openEmma from '../../assets/img/askEmma/open_emma.svg';
import closeEmma from '../../assets/img/askEmma/close_emma.svg';
import uploadIcon from '../../assets/img/askEmma/upload.svg';
import cameraIcon from '../../assets/img/askEmma/camera.svg';
import sendIcon from '../../assets/img/askEmma/send.svg';
import { useDispatch, useSelector } from "react-redux";
import { setShowEmma } from "../../features/askEmma/askEmmaSlice";
import { useLocation } from "react-router-dom";
import routes from "../../constants/Routes";
import AppContext from "../../contexts/AppContext";
import CONSTANT from "../../constants/Constant";
import GLOBAL from "../../global";
import { useTenantFeatures } from "../../features/tenantFeatures/useTenantFeature";

const NOT_ALLOWED_ROUTES = [routes.landing, routes.signUp, routes.signIn];

// Define the route where Emma chat should initially be hidden
const AUTO_HIDE_EMMA_CHAT_ROUTES = [routes.dashboard, routes.editAssessment];

const WebChatComponent = () => {
    const { authenticated } = useContext(AppContext);
    const location = useLocation();
    const askEmmaState = useSelector(state => state.askEmma);
    const chatBotState = useSelector(state => state.chatBot);
    const dashboardState = useSelector(state => state.dashboard);
    const editAssessmentState = useSelector(state => state.editAssessment);
    const dispatch = useDispatch();

    let showEmma = askEmmaState.showEmma;

    const [isChatContentVisible, setIsChatContentVisible] = useState(false);

    const wrapperRef = useRef(null);
    const { isAskEmmaEnabledInNewRegistrationPage, isSpinealignEnabled } = useTenantFeatures();

    const isChatbotOpen = chatBotState?.chatBotUIState?.showChatBot;
    const isDashboardBiometricOpen = dashboardState?.biometric?.openBiometricMovementAndCautionQuestionContainer;
    const isEditAssessmentBiometricOpen = editAssessmentState?.baseData?.openBiometricMovementAndCautionQuestionContainer;

    const isBiometricOpen = isDashboardBiometricOpen || isEditAssessmentBiometricOpen;
    const shouldHideForPBN = isSpinealignEnabled && (isChatbotOpen || isBiometricOpen);

    // Check if Ask EMMA should be shown based on route
    const isNewRegistrationPage = location.pathname === routes.newRegistration;
    const isOnboardingPage = location.pathname === routes.onboarding;
    const shouldShowAskEmma = (isNewRegistrationPage || isOnboardingPage)
        ? isAskEmmaEnabledInNewRegistrationPage
        : authenticated && CONSTANT.FEATURE_ENABLE.IsAskEmmaEnabled && !shouldHideForPBN;


    // Effect to manage initial chat content visibility based on the route
    useEffect(() => {
        if (AUTO_HIDE_EMMA_CHAT_ROUTES.includes(location.pathname) && CONSTANT.FEATURE_ENABLE.IsAskEmmaEnabled) {
            // If on the edit-assessment page, hide the chat content initially
            setIsChatContentVisible(false);
        }
    }, [location.pathname]);

    // Effect to manage initial chat content visibility based on the route
    useEffect(() => {
        if (askEmmaState?.eventName === 'patientPortal_posture-report' && CONSTANT.FEATURE_ENABLE.IsAskEmmaEnabled) {
            setIsChatContentVisible(true);
        }
    }, [askEmmaState]);

    return (
        <>
            {!NOT_ALLOWED_ROUTES.includes(location.pathname) && shouldShowAskEmma && (
                <div
                    className="ask-emma-wrapper"
                    ref={wrapperRef}
                    style={{
                        position: 'fixed',
                        right: '20px',
                        bottom: '0',
                        zIndex: 1051,
                    }}
                >
                    <div
                        style={{
                            position: 'fixed',
                            right: '20px',
                            bottom: '75px',
                            zIndex: 1051,
                        }}

                        className={`ask-emma ${showEmma && isChatContentVisible ? 'show' : 'hide'}`}>
                        <AskEmma />
                    </div>

                    <div
                        className={`emma-icon-container ${(isNewRegistrationPage || isOnboardingPage) ? 'with-questions-label' : ''}`}
                        style={{
                            right: '20px',
                            cursor: 'pointer',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(setShowEmma(!showEmma));
                            setIsChatContentVisible(true);
                        }}
                    >
                        {(isNewRegistrationPage || isOnboardingPage) && (
                            <span className="questions-label">
                                Questions?
                            </span>
                        )}
                        <div className="emma-icon-row">
                            <img
                                className="emma-icon"
                                src={showEmma ? closeEmma : openEmma}
                                alt="Close"
                            />

                            <span>
                                Ask EMMA
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const AskEmma = () => {
    const dispatch = useDispatch();
    const {
        handleFileUpload,
        handleSendMessage,
        uploadProgress,
        errorMessage,
        fileToUploadRef,
        showImgPreview,
        setShowImgPreview
    } = useFileUpload();

    const { directLine, locale, styleOptions } = useAskEmma();

    useEffect(() => {
        if (directLine) {
            WebChat.renderWebChat({ directLine, locale, styleOptions }, document.getElementById('webchat'));
            const directLineInitializedEvent = new Event('directLineInitialized');
            dispatchEvent(directLineInitializedEvent);
        }
    }, [directLine, locale, styleOptions]);


    const [userMessage, setUserMessage] = useState("");
    const [showCamera, setShowCamera] = useState(false);
    const videoRef = useRef(null);
    const fileInputRef = useRef(null);

    const handleCloseChat = () => {
        dispatch(setShowEmma(false));
    };



    const handleMessageChange = (event) => {
        setUserMessage(event.target.value); // Update user message state as they type
    };

    const handleEnterPress = async (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            await sendMessage();
        }
    }

    const clearMessageAfterSend = () => {
        setUserMessage('');
        setShowImgPreview(false);
        fileToUploadRef.current = null;
    }

    const sendMessage = async () => {
        await handleSendMessage(userMessage, fileToUploadRef.current, clearMessageAfterSend);
    };

    const handleCloseCamera = () => {
        setShowCamera(false);
    };

    const handleCaptureFile = async (file) => {
        fileToUploadRef.current = file;
        setShowCamera(false);
        setShowImgPreview(true);
    };

    const handleFileButtonClick = () => {
        fileInputRef.current.click();
    };

    // Prevent drag initiation for interactive elements
    const stopPropagation = (e) => {
        e.stopPropagation();
    };

    return (
        <div className="webchat-wrapper" onMouseDown={stopPropagation}>
            <div className="ask-emma-header">
                <h3 className="ask-emma-title"></h3>
                <IconCloseButton closeHandler={handleCloseChat} styleClass="ask-emma-close-btn" />
            </div>

            <div id="webchat" role="main"></div>

            <div className="custom-input-container">
                <div className="button-row">
                    {/*<button id="customFileUploadButton" onClick={handleFileButtonClick} aria-label="Upload File">*/}
                    {/*    <i className="fas fa-upload"></i> Upload*/}
                    {/*    {*/}
                    {/*        showImgPreview*/}
                    {/*            ? <img className="thumbnail-xsm ml-1" src={fileToUploadRef.current} alt="Preview" />*/}
                    {/*            : null*/}
                    {/*    }*/}
                    {/*</button>*/}
                    {/*<button id="customCameraButton" onClick={() => setShowCamera(true)} aria-label="Capture Image">*/}
                    {/*    <i className="fas fa-camera"></i> Camera*/}
                    {/*</button>*/}
                </div>
                <div className="input-row">
                    {/*<button id="customFileUploadButton" onClick={handleFileButtonClick} aria-label="Upload File">*/}
                    {/*    <img src={uploadIcon} alt="img" />*/}
                    {/*    {*/}
                    {/*        showImgPreview*/}
                    {/*            ? <img className="thumbnail-xsm ml-1" src={fileToUploadRef.current} alt="Preview" />*/}
                    {/*            : null*/}
                    {/*    }*/}
                    {/*</button>*/}
                    {/*<button id="customCameraButton" onClick={() => setShowCamera(true)} aria-label="Capture Image">*/}
                    {/*    <img src={cameraIcon} alt="img" />*/}
                    {/*</button>*/}
                    <input
                        id="customTextInput"
                        placeholder="Type your message here..."
                        aria-label="Type your message here"
                        value={userMessage}
                        onChange={handleMessageChange}
                        onKeyDown={handleEnterPress}
                    />
                    <button id="customSendButton" onClick={sendMessage} aria-label="Send Message">
                        <img src={sendIcon} alt="img" />
                    </button>
                </div>
                <div id="uploadProgressBar" aria-live="polite">
                    <div id="uploadProgress" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <div id="errorMessage" className="error-message" role="alert" aria-live="assertive">
                    {errorMessage}
                </div>
            </div>

            {showCamera && (
                <CameraCapture
                    videoRef={videoRef}
                    onCapture={handleCaptureFile}
                    onClose={handleCloseCamera}
                />
            )}

            {/* Hidden file input */}
            <input
                ref={fileInputRef}  // Attach ref to the file input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.bmp" // Restricts file types
                onChange={handleFileUpload} // Handle file selection
                aria-label="File Upload Input"
                style={{ display: "none" }} // Hide the file input
            />
        </div>
    )
}

export default WebChatComponent;
